import React, { useRef, useState } from 'react';
import { smartUpload, openFileInNewTab } from '../../api/filesApi';
import FilePicker from '../../components/FilePicker';
import { NotYetImplemented } from '../../components/ui';
import { SOURCES, sourcesFor } from './runInputs';

const CONTROL = 'w-full border-1.5 border-ink rounded-[10px] bg-card text-ink text-sm px-3 py-2 focus-ring';
const LINK = 'underline text-ink text-xs font-semibold focus-ring';

function Chip({ source, pressed, onClick }) {
  const btn = (
    <button
      type="button"
      aria-pressed={pressed}
      onClick={onClick}
      className={`h-8 px-3 text-xs font-medium rounded-[10px] border-1.5 border-ink focus-ring ${pressed ? 'bg-hl' : 'bg-card'} text-ink`}
    >
      {source.label}
    </button>
  );
  return source.unfinished ? <NotYetImplemented inline feature={source.unfinished}>{btn}</NotYetImplemented> : btn;
}

export default function InputField({ input, value, carriedFrom, invalid = false, onChange }) {
  const { name, label, hint, required, kind, options } = input;
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState(null);
  const [picker, setPicker] = useState(null); // 'mydata' | 'output' | null
  const [pasting, setPasting] = useState(false);
  const id = `input-${name}`;
  const chips = sourcesFor(kind);
  const obj = value && typeof value === 'object' ? value : null;
  const isFile = obj && obj.source !== 'paste';
  const pastedText = obj?.source === 'paste' ? obj.text : '';
  const showTextarea = kind === 'text' || pasting;

  const onFileChosen = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    setUploadError(null);
    try {
      const result = await smartUpload(file, null, 100 * 1024 * 1024);
      onChange({ source: 'upload', file_id: result.file_id, filename: file.name });
    } catch (err) {
      console.error('File upload failed:', err);
      setUploadError(`Couldn't upload ${file.name}.`);
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const click = (key) => {
    if (key === 'upload') fileRef.current?.click();
    else if (key === 'mydata' || key === 'output') setPicker(key);
    else if (key === 'paste') setPasting(true);
  };
  const pressed = (key) => (key === 'paste' ? showTextarea || obj?.source === 'paste' : obj?.source === key);

  let control = null;
  if (kind === 'number' || kind === 'date') {
    control = (
      <input id={id} type={kind} className={CONTROL} value={value ?? ''}
        onChange={(e) => onChange(e.target.value === '' ? null : (kind === 'number' ? Number(e.target.value) : e.target.value))} />
    );
  } else if (kind === 'select') {
    control = (
      <select id={id} className={CONTROL} value={value ?? ''} onChange={(e) => onChange(e.target.value || null)}>
        <option value="" />
        {(options || []).map((o) => <option key={o} value={o}>{o}</option>)}
      </select>
    );
  }

  return (
    <div className="flex flex-col gap-2" data-testid={`input-${name}`}>
      <div className="flex flex-wrap items-baseline gap-2">
        <label htmlFor={control || showTextarea ? id : undefined} className="font-semibold text-sm text-ink">{label}</label>
        <span className="text-xs font-bold uppercase tracking-wider text-ink">{required ? 'REQUIRED' : 'OPTIONAL'}</span>
        {hint ? <span className="text-xs text-pencil">{hint}</span> : null}
        {invalid ? <span className="text-xs font-bold text-status-failed">Needed</span> : null}
      </div>

      {control}

      {chips.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {chips.map((key) => (
            <Chip key={key} source={SOURCES.find((s) => s.key === key)} pressed={pressed(key)} onClick={() => click(key)} />
          ))}
        </div>
      )}

      {kind === 'file' && (
        <input ref={fileRef} type="file" className="hidden" tabIndex={-1} aria-hidden="true" onChange={onFileChosen} />
      )}

      {uploadError && <div role="alert" className="text-sm text-status-failed">{uploadError}</div>}

      {uploading && <div className="text-sm text-pencil">Uploading…</div>}

      {isFile && !uploading && (
        <div className="flex items-center gap-3 text-sm text-ink">
          <span className="font-medium">{obj.filename}</span>
          <button type="button" className={LINK} onClick={() => openFileInNewTab(obj.file_id)}>Open</button>
          <button type="button" className={LINK} onClick={() => onChange(null)}>Replace</button>
        </div>
      )}

      {showTextarea && (
        <textarea id={id} rows={kind === 'text' ? 2 : 5} className={CONTROL} value={pastedText}
          onChange={(e) => onChange(e.target.value ? { source: 'paste', text: e.target.value } : null)} />
      )}

      {!showTextarea && pastedText && (
        <div className="flex items-center gap-3 text-sm text-ink">
          <span>{pastedText.slice(0, 80)}</span>
          <button type="button" className={LINK} onClick={() => setPasting(true)}>Edit</button>
        </div>
      )}

      {carriedFrom ? <div className="text-xs text-pencil">{`From ${carriedFrom}'s run`}</div> : null}

      <FilePicker
        show={!!picker}
        onClose={() => setPicker(null)}
        onSelect={(file) => {
          setUploadError(null);
          onChange({ source: picker, file_id: file.file_id, filename: file.filename });
          setPicker(null);
        }}
        multiSelect={false}
        fileTypeFilter={picker === 'output' ? 'output' : null}
      />
    </div>
  );
}
